import { Button } from "@/components/ui/button";
import { ArForm, ArFormField } from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import FindOneTermsAndCondition from "@/v2/api/terms-and-conditions/FindOne";
import {
    CreateTermsAndConditionSchema,
    TermsAndConditionFormSchema,
    UpdateTermsAndConditionSchema,
} from "@/v2/schema/terms-and-conditions";
import CreateTermsAndCondition from "@/v2/api/terms-and-conditions/Create";
import UpdateTermsAndCondition from "@/v2/api/terms-and-conditions/Update";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function TermsAndConditionsForm({ id }: { id?: string }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const query = useQuery({
        queryKey: ["terms-and-conditions", id],
        queryFn: () => FindOneTermsAndCondition(id!),
        enabled: !!id,
    });

    const form = useForm<TermsAndConditionFormSchema>({
        resolver: zodResolver(TermsAndConditionFormSchema),
        defaultValues: {
            template_name: "",
            terms_and_conditions: [{ value: "" }],
        },
    });

    const { reset } = form;

    const {
        fields: termsAndConditionFields,
        append: termsAndConditionAppend,
        remove: termsAndConditionRemove,
    } = useFieldArray({
        control: form.control,
        name: "terms_and_conditions",
    });

    useEffect(() => {
        if (query.data) {
            reset({
                template_name: query?.data?.template_name || "",
                terms_and_conditions: query.data.terms_and_conditions?.map(
                    (terms: string) => ({
                        value: terms,
                    }),
                ) ?? [{ value: "" }],
            });
        }
    }, [query.data, reset]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateTermsAndConditionSchema) =>
            CreateTermsAndCondition(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: UpdateTermsAndConditionSchema) =>
            UpdateTermsAndCondition({ id: id!, data: data }),
    });

    const handleFormSubmit = (data: TermsAndConditionFormSchema) => {
        const mutation = id ? EditMutation : CreateMutation;
        const action = id ? "updated" : "created";

        const mutationData = {
            template_name: data.template_name,
            terms_and_conditions: data.terms_and_conditions.map(
                (terms) => terms.value,
            ),
        };

        mutation.mutate(mutationData, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "creating"} the terms and conditions template, we encountered an issue.`,
                });
            },
            onSuccess: (res) => {
                toast.success(
                    `Terms and Conditions Template ${action} successfully`,
                );
                queryClient.invalidateQueries({
                    queryKey: ["terms-and-conditions"],
                });
                if (res?.id)
                    navigate(`/templates/terms-and-conditions/${res?.id}`);
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">
                        Failed to load terms and conditions template
                    </p>
                    <Button>Go Back</Button>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={TermsAndConditionFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<TermsAndConditionFormSchema>
                        label="Template Name"
                        name="template_name"
                        placeholder="Enter template name"
                    />
                </div>

                <div className="space-y-4">
                    <div className="space-y-4 rounded-lg border p-4">
                        <div className="flex flex-col items-center justify-between md:flex-row">
                            <h3 className="text-lg font-semibold">
                                Terms And Condition
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    termsAndConditionAppend({ value: "" })
                                }
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add Term
                            </Button>
                        </div>

                        <div className="space-y-3">
                            {termsAndConditionFields.map((field, index) => (
                                <div
                                    key={field.id}
                                    className="flex items-center gap-2"
                                >
                                    <div className="flex-1">
                                        <ArFormField<TermsAndConditionFormSchema>
                                            label={`Terms And Condition ${index + 1}`}
                                            name={`terms_and_conditions.${index}.value`}
                                            placeholder="Enter terms and condition"
                                        />
                                    </div>
                                    {termsAndConditionFields.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                termsAndConditionRemove(index)
                                            }
                                            className="mt-8" // Align with input field (accounting for label height)
                                        >
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Terms And Conditions Template"
                              : "Create Terms And Conditions Template"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
